import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'
import { ORDERS_ENDPOINTS } from './orders.endpoints'
import type {
  OrderDetailResponse,
  OrderListParams,
  PaginatedOrderResponse,
  ShipmentTrackingResponse,
} from './orders.types'

// --- Service Functions ---

export const getOrdersList = async (params?: OrderListParams): Promise<PaginatedOrderResponse> => {
  const response = await apiClient.get<PaginatedOrderResponse>(ORDERS_ENDPOINTS.LIST, { params })
  return response.data
}

export const getOrderDetail = async (orderNumber: string): Promise<OrderDetailResponse> => {
  const response = await apiClient.get<OrderDetailResponse>(ORDERS_ENDPOINTS.DETAIL(orderNumber))
  return response.data
}

export const getShipmentTracking = async (
  orderNumber: string,
  shipmentNumber: string,
): Promise<ShipmentTrackingResponse> => {
  const response = await apiClient.get<ShipmentTrackingResponse>(ORDERS_ENDPOINTS.TRACKING(orderNumber, shipmentNumber))
  return response.data
}

// --- TanStack Query Hooks ---

export const ORDERS_QUERY_KEYS = {
  LIST: ['orders', 'list'],
  DETAIL: (orderNumber: string) => ['orders', 'detail', orderNumber],
  TRACKING: (orderNumber: string, shipmentNumber: string) => ['orders', 'tracking', orderNumber, shipmentNumber],
} as const

export const useOrdersListQuery = (
  params?: OrderListParams,
  options?: Omit<UseQueryOptions<PaginatedOrderResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...ORDERS_QUERY_KEYS.LIST, params],
    queryFn: () => getOrdersList(params),
    ...options,
  })
}

export const useOrderDetailQuery = (
  orderNumber: string,
  options?: Omit<UseQueryOptions<OrderDetailResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: ORDERS_QUERY_KEYS.DETAIL(orderNumber),
    queryFn: () => getOrderDetail(orderNumber),
    enabled: !!orderNumber,
    ...options,
  })
}

export const useShipmentTrackingQuery = (
  orderNumber: string,
  shipmentNumber: string,
  options?: Omit<UseQueryOptions<ShipmentTrackingResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: ORDERS_QUERY_KEYS.TRACKING(orderNumber, shipmentNumber),
    queryFn: () => getShipmentTracking(orderNumber, shipmentNumber),
    enabled: !!orderNumber && !!shipmentNumber,
    ...options,
  })
}
